
import { defineComponent } from "vue";
import { User } from "@/types/User";
import { get, identity, isEqual, pick, pickBy } from "lodash-es";
import { Confirmation, humanize, initials, timeAgo } from "@/util/helpers";
import { useAppConfig } from "@/config";
import {
  useAuth,
  useCognitoAdmin,
  useLayout,
  useOrgSettings,
  useUserAdmin,
} from "@/services";
import { mapState } from "pinia";
import moment from "moment";
import { CognitoIdentityServiceProvider } from "aws-sdk";

interface UserForm {
  username?: string;
  password?: string;
  organisationName?: string;
  coverHolder?: string;
  groups: string[];
  role?: string;
}

type FilterField =
  | "email"
  | "organisationName"
  | "coverHolder"
  | "username"
  | "status"
  | "role"
  | "name";

export default defineComponent({
  name: "Users",
  data: () => ({
    adminRole: "GammaAdmin",
    allowMultipleGroups: false,
    fields: [
      { key: "Username", sortable: true },
      { key: "organisationName", label: "Organisation", sortable: false },
      { key: "lastLogin", label: "Last Login", sortable: false },
      { key: "coverHolder", label: "Cover Holder", sortable: false },
      { key: "groups", label: "Groups", sortable: false },
      { key: "verifiedEmail", label: "Verified Email", sortable: true },
      "Actions",
    ],
    filterOptions: [
      "username",
      "email",
      "organisationName",
      "group", //, 'role'
    ],
    filterField: "username",
    searchTerm: "",
    hoveredRow: "",
    savedUserData: null as null | User,
    createdUser: null as null | User,
    repeatPassword: "",
    userForm: {
      username: "",
      role: "",
      groups: [],
      coverHolder: "",
      organisationName: "",
    } as UserForm,
    originalUserGroups: [] as string[],
    currentPage: 1,
    perPage: 10,
  }),
  computed: {
    ...mapState(useUserAdmin, ["organisationsList", "coverHolderList"]),
    ...mapState(useOrgSettings, ["showCoverHolder"]),
    selectedUserGroup: {
      get(): string {
        return this.userForm.groups[0];
      },
      set(val: string) {
        this.userForm.groups = [val];
      },
    },
    selectedUserGroups: {
      get(): string[] {
        return this.userForm.groups;
      },
      set(val: string[]) {
        this.userForm.groups = val;
      },
    },
    users(): User[] {
      return useCognitoAdmin().users as User[];
    },
    groups(): string[] {
      return this.currentUserGroups.includes(this.adminRole)
        ? useCognitoAdmin().groups
        : useCognitoAdmin().groups.filter((g) => g !== this.adminRole);
    },
    currentUser(): string {
      return useCognitoAdmin().currentUser;
    },
    currentUserGroups(): string[] {
      return useCognitoAdmin().currentUserGroups;
    },
    filteredUsers(): User[] {
      return this.searchTerm && this.filterField
        ? this.users.filter(
            (user) =>
              user[this.filterField as FilterField]
                .toLowerCase()
                .indexOf(this.searchTerm.toLowerCase().trim()) > -1
          )
        : this.users;
    },
    userEditModalTitle(): string {
      return this.savedUserData
        ? "Edit " + this.savedUserData.name
        : "Add new user";
    },
    tableFields(): any {
      const hasLastLogin = this.users.some((x) => !!x.lastLogin);

      if (hasLastLogin && this.showCoverHolder) {
        return this.fields;
      }

      let newFields = this.fields;

      if (!this.showCoverHolder) {
        newFields = newFields.filter((f: any) => f.key !== "coverHolder");
      }

      if (!hasLastLogin) {
        newFields = newFields.filter((f: any) => f.key !== "lastLogin");
      }

      return newFields;
    },
    editedUser: {
      get(): UserForm {
        return this.userForm;
      },
      set(user: User | null) {
        this.userForm = {
          username: user?.username || "",
          role: user?.role || "",
          groups: user?.groups || [],
          coverHolder: user?.coverHolder || "",
          organisationName: user?.organisationName || "",
        };
      },
    },
    hasPermission(): boolean {
      return this.currentUserGroups.some((g) =>
        useAppConfig().adminRoles.includes(g)
      );
    },
    isSaveDisabled(): boolean {
      // Check if updating user or creating new user
      return this.savedUserData
        ? isEqual(
            this.editedUser,
            pick(this.savedUserData, [
              "username",
              "role",
              "groups",
              "coverHolder",
              "organisationName",
            ])
          )
        : !Object.values(
            pick(this.userForm, ["username", "groups", "organisationName"])
          ).every(
            (v) =>
              v && (Array.isArray(v) ? v.filter((g) => g).length : v.length)
          ); // Check mandatory userform values to ensure no empty values
    },
    showMultipleGroupOption(): boolean {
      return false;
    },
    validateUser(): boolean {
      // todo: new user form validation
      return true;
    },
    rows(): number {
      return this.filteredUsers.length;
    },
  },
  mounted() {
    useLayout().isBottomDrawerOpen = false;
    if (useAuth().isLoggedIn) {
      useLayout().isRightSidebarOpen = true;
    }
    const {
      getProvider,
      getCurrentUserGroups,
      loadUsers,
      currentCredentials,
      getCurrentUser,
    } = useCognitoAdmin();
    getProvider().then(() => {
      getCurrentUserGroups();
      loadUsers();
      currentCredentials();
      getCurrentUser();
    });

    useUserAdmin().listOrganisations();
    useUserAdmin().listCoverHolders({ attributeName: "cover_holder" });
  },
  methods: {
    humanize,
    initials,
    timeAgo,
    isRowHovered(user: User) {
      return this.hoveredRow === user.username;
    },
    displayGroup(groups: []): string {
      return groups.length > 0 ? groups.join(", ") : " - ";
    },
    onRowHover(user: User) {
      this.hoveredRow = user.username;
    },
    onRowHoverOut(user: User) {
      if (this.hoveredRow === user.username) {
        this.hoveredRow = "";
      }
    },
    getGroupOptions() {
      return this.groups.map((g) => ({ text: g, value: g }));
    },
    changeFilter(option: string) {
      this.filterField = option;
      const searchBox = get(this.$refs, "searchBox.$el");
      this.$nextTick().then(() => {
        if (searchBox instanceof HTMLInputElement) {
          searchBox.focus();
        }
      });
    },
    makeToast(variant: string, message: string, content: string) {
      this.$bvToast.toast(content, {
        title: message,
        variant: variant,
        solid: true,
      });
    },
    getLastLoginDate(user: User): string {
      return user.lastLogin
        ? moment(user.lastLogin).format("DD/MM/YYYY HH:mm")
        : "";
    },
    resetUserPassword(user: User) {
      Confirmation.fire({
        icon: "question",
        html: "Reset <strong>" + user.name + "</strong>`s password?",
        confirmButtonText: "Reset password",
        confirmButtonAriaLabel: "Confirm password testData",
        cancelButtonAriaLabel: "Decline password testData",
      }).then((r) => {
        if (r.isConfirmed) {
          useCognitoAdmin().resetPassword({ Username: user.username });
        }
      });
    },
    resetForm() {
      this.originalUserGroups = [];
      this.savedUserData = null;
      this.createdUser = null;
      this.userForm = {
        username: "",
        organisationName: "",
        groups: [],
      };
    },
    openUserEditModal(user: User | null) {
      this.resetForm();
      if (!user) {
        const _data = {
          username: "",
          email: "",
          verifiedEmail: "false",
          role: "",
          groups: [],
          coverHolder: "",
        };
        this.createdUser = new User(_data);
        this.$bvModal.show("user-edit");
      } else {
        this.originalUserGroups = user.groups;
        if (this.allowMultipleGroups) {
          this.selectedUserGroups = user.groups;
        } else {
          this.selectedUserGroup = user.groups[0];
        }
        this.savedUserData = user;
        this.editedUser = user;
        this.$bvModal.show("user-edit");
      }
    },
    saveUserEdit() {
      if (this.savedUserData) {
        const editValues = pickBy(this.userForm, identity);
        if (editValues.username) {
          this.updateUserGroups(editValues);
          const editPayload = {
            Username: editValues.username,
            attributes: [
              {
                Name: "custom:organisation",
                Value: editValues.organisationName || "",
              },
            ],
          };
          if (this.showCoverHolder) {
            editPayload.attributes.push({
              Name: "custom:cover_holder",
              Value: editValues.coverHolder || "",
            });
          }
          useCognitoAdmin()
            .updateUserAttributes({
              Username: editPayload.Username,
              UserAttributes: editPayload.attributes,
            } as Partial<CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesRequest>)
            .then(() => {
              this.$bvModal.hide("user-edit");
              this.makeToast(
                "success",
                `Successfully updated ${this.savedUserData?.username}`,
                "Request completed"
              );
              useCognitoAdmin().getUser({
                Username: this.savedUserData?.username as string,
              });
              this.savedUserData = null;
            })
            .catch(() =>
              this.makeToast(
                "danger",
                `Failed to update ${this.savedUserData?.username}`,
                "Request failed"
              )
            );
        }
      } else {
        this.saveCreatedUser();
      }
    },
    async updateUserGroups(editValues: Partial<UserForm>) {
      const updateGroup = (groupName: string) => {
        if (editValues.username && groupName) {
          useCognitoAdmin()
            .addUserToGroup({
              Username: editValues.username,
              GroupName: groupName,
            })
            .then(() =>
              this.makeToast(
                "success",
                `Added user: ${editValues.username} to group '${groupName}'`,
                "Request Completed"
              )
            )
            .catch(() =>
              this.makeToast(
                "danger",
                `Failed to add user: ${editValues.username} to group '${groupName}}'`,
                "Request Failed"
              )
            );
        }

        this.originalUserGroups
          .filter((group) =>
            this.allowMultipleGroups
              ? !this.selectedUserGroups.includes(group)
              : this.selectedUserGroup !== group
          )
          .forEach(async (group: string) => {
            if (editValues.username) {
              await useCognitoAdmin().removeUserFromGroup({
                Username: editValues.username,
                GroupName: group,
              });
            }
          });
      };

      if (this.allowMultipleGroups) {
        this.selectedUserGroups.forEach((group) => updateGroup(group));
      } else {
        updateGroup(this.selectedUserGroup);
      }
    },
    saveCreatedUser() {
      if (this.createdUser) {
        const userAttributes = pickBy(this.userForm, identity);
        if (userAttributes.username) {
          const createPayload = {
            Username: userAttributes.username,
            UserAttributes: [
              {
                Name: "custom:organisation",
                Value: userAttributes.organisationName,
              },
              { Name: "email", Value: userAttributes.username },
              { Name: "email_verified", Value: "true" },
            ],
            ValidationData: [{ Name: "email", Value: userAttributes.username }],
          };
          if (this.showCoverHolder && userAttributes.coverHolder) {
            createPayload.UserAttributes.push({
              Name: "custom:cover_holder",
              Value: userAttributes.coverHolder,
            });
          }
          useCognitoAdmin()
            .createUser(
              createPayload as Partial<CognitoIdentityServiceProvider.Types.AdminCreateUserRequest>
            )
            .then(() => {
              this.$bvModal.hide("user-edit");
              this.makeToast(
                "success",
                `Successfully created ${this.createdUser?.username}`,
                "Request completed"
              );
              this.updateUserGroups(userAttributes);
              useCognitoAdmin().getUser({
                Username: this.createdUser?.username as string,
              });
              this.savedUserData = null;
              useCognitoAdmin().loadUsers();
            })
            .catch(() =>
              this.makeToast(
                "danger",
                `Failed to create ${this.createdUser?.username}`,
                "Request failed"
              )
            );
        }
      }
    },
    deleteUser(user: User) {
      Confirmation.fire({
        icon: "error",
        iconHtml: "!",
        html: "Delete <strong>" + user.name + "</strong>?",
        confirmButtonText: "Delete user",
        confirmButtonAriaLabel: "Confirm user deletion",
        cancelButtonAriaLabel: "Decline user deletion",
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-outline-info mr-3",
        },
        focusConfirm: false,
        focusCancel: true,
      }).then((r) => {
        if (r.isConfirmed) {
          useCognitoAdmin()
            .deleteUser({ Username: user.username })
            .then(() => {
              this.makeToast(
                "success",
                `Successfully deleted ${user.username}.`,
                "Request Completed"
              );
            });
        }
      });
    },
    unlockUser(user: User) {
      Confirmation.fire({
        icon: "question",
        html: "Unlock <strong>" + user.name + "</strong>`s account?",
        confirmButtonText: "Unlock account",
        confirmButtonAriaLabel: "Confirm account unlock",
        cancelButtonAriaLabel: "Cancel account unlock",
      }).then((r) => {
        console.log("ToDo: implement unlockUser() method", r);
      });
    },
    scroll() {
      this.$bus.$emit("scroll:top");
    },
  },
});
